import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, MapPin, Phone, Loader2, CheckCircle, Clock, DollarSign, 
  Calendar, MessageSquare, Shield, CreditCard, ChevronDown, ChevronUp,
  Star, User, AlertTriangle, Video, Camera, FileText, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import BookingCalendar from '../components/BookingCalendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';
import { StarRating, RatingSummary } from '../components/StarRating';
import { format } from 'date-fns';
import TurnstileWidget from '../components/TurnstileWidget';
import AnimalPicker from '../components/animals/AnimalPicker';
import { ANIMAL_LABELS, ANIMAL_PLURALS } from '../lib/animals';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category display names
const CATEGORY_LABELS = {
  FARRIER: 'Farrier',
  VET: 'Veterinarian',
  RIDING_INSTRUCTOR: 'Riding Instructor',
  TRAINER: 'Horse Trainer',
  HAULING: 'Horse Hauling',
  BARN_MANAGEMENT: 'Barn Management',
  FENCE_REPAIR: 'Fence Repair',
  RANCH_SERVICE: 'Ranch Services',
  EQUIPMENT_WORK: 'Equipment Work',
  GUIDED_TRAIL_RIDES: 'Guided Trail Rides',
  RIDING_STABLE: 'Riding Stable',
  STABLE_SERVICES: 'Stable Services'
};

export default function ProviderPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const bookingFormRef = useRef(null);
  
  const [provider, setProvider] = useState(null);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [sharedDocs, setSharedDocs] = useState([]);
  const [routeZones, setRouteZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [offersConsultations, setOffersConsultations] = useState(false);
  
  // Booking form state
  const [selectedService, setSelectedService] = useState('');
  const [selectedZone, setSelectedZone] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [horseCount, setHorseCount] = useState(1);
  const [riderCount, setRiderCount] = useState('');
  const [horseName, setHorseName] = useState('');
  const [horseNotes, setHorseNotes] = useState('');
  const [animalSelection, setAnimalSelection] = useState({
    mode: 'group',
    selectedAnimalIds: [],
    animalType: 'HORSE',
    animalQuantity: 1,
    groupDescription: '',
  });
  const [notes, setNotes] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [locationNotes, setLocationNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [routeZoneMessage, setRouteZoneMessage] = useState('');
  
  // Riding lesson specific fields
  const [studentName, setStudentName] = useState('');
  const [studentExperienceLevel, setStudentExperienceLevel] = useState('');
  const [lessonNotes, setLessonNotes] = useState('');
  
  // Turnstile CAPTCHA
  const [turnstileToken, setTurnstileToken] = useState(null);
  const handleTurnstileToken = useCallback((token) => setTurnstileToken(token), []);

  const isRidingInstructor = provider?.providerCategory === 'RIDING_INSTRUCTOR';
  const isFarrierOrTrainer = ['FARRIER', 'TRAINER', 'VET'].includes(provider?.providerCategory);
  const hasRouteZones = routeZones.length > 0;

  useEffect(() => {
    loadProviderData();
  }, [slug]);

  useEffect(() => {
    if (selectedService && selectedDate) {
      loadAvailableSlots();
    }
  }, [selectedService, selectedDate, selectedZone]);

  const loadProviderData = async () => {
    try {
      setLoading(true);
      const providerRes = await axios.get(`${API_URL}/api/providers/slug/${slug}`);
      setProvider(providerRes.data);
      
      const servicesRes = await axios.get(`${API_URL}/api/providers/${providerRes.data.id}/services`);
      setServices(servicesRes.data);
      
      const reviewsRes = await axios.get(`${API_URL}/api/providers/${providerRes.data.id}/reviews`);
      setReviews(reviewsRes.data);

      try {
        const galleryRes = await axios.get(`${API_URL}/api/providers/${providerRes.data.id}/gallery`);
        setGalleryPhotos(galleryRes.data || []);
      } catch { /* gallery optional */ }

      try {
        const docsRes = await axios.get(`${API_URL}/api/providers/${providerRes.data.id}/documents`);
        setSharedDocs(docsRes.data || []);
      } catch { /* documents optional */ }
      
      const zonesRes = await axios.get(`${API_URL}/api/providers/${providerRes.data.id}/route-zones`);
      setRouteZones(zonesRes.data);
      
      // Check if provider offers consultations
      try {
        const consultRes = await axios.get(`${API_URL}/api/providers/${providerRes.data.id}/consultation-availability`);
        setOffersConsultations(consultRes.data?.offersConsultations || false);
      } catch (e) {
        setOffersConsultations(false);
      }
    } catch (error) {
      console.error('Error loading provider:', error);
      toast.error('Failed to load provider information');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableSlots = async () => {
    if (!selectedService || !selectedDate) return;
    
    // Route zone filtering
    if (hasRouteZones && selectedZone && selectedZone !== 'other') {
      const selectedDateObj = new Date(selectedDate + 'T12:00:00');
      const dayOfWeek = selectedDateObj.getDay();
      const zone = routeZones.find(z => z.zoneName === selectedZone);
      
      if (zone && zone.dayOfWeek !== dayOfWeek) {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        setRouteZoneMessage(
          `${selectedZone} is served on ${days[zone.dayOfWeek]}s. Please select a ${days[zone.dayOfWeek]} for this location.`
        );
        setAvailableSlots([]);
        return;
      } else {
        setRouteZoneMessage('');
      }
    }
    
    try {
      setLoadingSlots(true);
      const response = await axios.get(
        `${API_URL}/api/providers/${provider.id}/availability`,
        { params: { date: selectedDate, serviceId: selectedService } }
      );
      setAvailableSlots(response.data);
    } catch (error) {
      console.error('Error loading slots:', error);
      toast.error('Failed to load available times');
    } finally {
      setLoadingSlots(false);
    }
  };

  const scrollToBooking = () => {
    bookingFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const formatPrice = (cents, model) => {
    if (!cents) return 'Contact for pricing';
    const base = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
    const suffix = {
      PER_HORSE: '/horse', PER_HOUR: '/hr', PER_MONTH: '/mo', PER_VISIT: '/visit'
    }[model] || '';
    return base + suffix;
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const getSelectedServiceData = () => {
    return services.find(s => s.id === selectedService);
  };

  const calculateDepositAmount = (service) => {
    if (!service?.depositEnabled) return null;
    
    if (service.depositType === 'FIXED') {
      return service.depositFixedAmountCents;
    } else if (service.depositType === 'PERCENTAGE') {
      return Math.round((service.basePriceCents * service.depositPercentage) / 100);
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedService || !selectedDate || !selectedTime || !clientName || !clientPhone || !locationAddress) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (getSelectedServiceData()?.maxGroupSize && (!riderCount || parseInt(riderCount) < 1)) {
      toast.error('Please enter the number of riders in your group');
      return;
    }

    if (!turnstileToken) {
      toast.error('Please complete the CAPTCHA verification');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Derive horseCount from animal selection for backward compatibility
      const isSaved = animalSelection.mode === 'saved' && animalSelection.selectedAnimalIds.length > 0;
      const derivedCount = isSaved
        ? animalSelection.selectedAnimalIds.length
        : animalSelection.animalQuantity || horseCount || 1;

      const bookingData = {
        providerId: provider.id,
        serviceId: selectedService,
        clientName,
        clientPhone,
        clientEmail: clientEmail || undefined,
        startDateTime: `${selectedDate}T${selectedTime}`,
        horseCount: derivedCount,
        horseName: horseName || undefined,
        horseNotes: horseNotes || undefined,
        locationAddress,
        jobLocation: locationAddress,
        locationNotes: locationNotes || undefined,
        notes: notes || undefined,
        routeZone: selectedZone && selectedZone !== 'other' ? selectedZone : undefined,
        // New animal selection (flat schema)
        selectedAnimalIds: isSaved ? animalSelection.selectedAnimalIds : undefined,
        animalType: !isSaved ? animalSelection.animalType : undefined,
        animalQuantity: !isSaved ? animalSelection.animalQuantity : undefined,
        groupDescription: !isSaved ? (animalSelection.groupDescription || undefined) : undefined,
        riderCount: getSelectedServiceData()?.maxGroupSize && riderCount ? parseInt(riderCount) : undefined,
      };
      
      // Add riding lesson fields
      if (isRidingInstructor) {
        bookingData.studentName = studentName || undefined;
        bookingData.studentExperienceLevel = studentExperienceLevel || undefined;
        bookingData.lessonNotes = lessonNotes || undefined;
      }
      
      await axios.post(`${API_URL}/api/bookings/create`, bookingData, {
        headers: { 'X-Turnstile-Token': turnstileToken }
      });
      setBookingSuccess(true);
      toast.success('Booking request submitted successfully!');
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit booking');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-lg text-muted-foreground">Provider not found</p>
        <Button onClick={() => navigate('/')}>Back to Home</Button>
      </div>
    );
  }

  // Check if provider account is not active
  const isProviderActive = provider.accountStatus === 'ACTIVE';

  if (bookingSuccess) {
    return (
      <div className="min-h-screen bg-background">
        <nav className="bg-card border-b">
          <div className="max-w-3xl mx-auto px-4 py-4">
            <Logo variant="compact" size="sm" />
          </div>
        </nav>
        <div className="max-w-lg mx-auto px-4 py-12">
          <Card>
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="bg-green-100 rounded-full p-4">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
              </div>
              <CardTitle className="text-2xl">Booking Request Sent!</CardTitle>
              <CardDescription className="text-base mt-2">
                {provider.businessName} will review your request and contact you shortly.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted p-4 rounded-lg text-sm space-y-1">
                <p><strong>Date:</strong> {format(new Date(selectedDate + 'T12:00:00'), 'EEEE, MMMM d, yyyy')}</p>
                <p><strong>Time:</strong> {selectedTime}</p>
                <p><strong>Service:</strong> {getSelectedServiceData()?.name}</p>
              </div>
              <div className="flex gap-3">
                <Button onClick={() => navigate('/')} variant="outline" className="flex-1">
                  Back to Home
                </Button>
                <Button onClick={() => window.location.reload()} className="flex-1">
                  Book Another
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const displayedReviews = showAllReviews ? reviews : reviews.slice(0, 3);

  return (
    <div className="min-h-screen bg-background">
      {/* Sticky Header */}
      <nav className="bg-card border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex justify-between items-center h-14">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="gap-1"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <Logo variant="compact" size="sm" />
            <Button
              size="sm"
              onClick={scrollToBooking}
              data-testid="header-book-button"
            >
              Book Now
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 pb-24">
        {/* Provider Hero Section */}
        <div className="py-6 border-b">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div>
              <Badge variant="secondary" className="mb-2">
                {CATEGORY_LABELS[provider.providerCategory] || provider.providerCategory}
              </Badge>
              <h1 className="text-2xl sm:text-3xl font-bold" data-testid="provider-name">
                {provider.businessName}
              </h1>
              <div className="mt-2" data-testid="provider-rating">
                <RatingSummary 
                  averageRating={provider.averageRating} 
                  reviewCount={provider.reviewCount}
                  size="md"
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Button 
                size="lg" 
                onClick={scrollToBooking}
                className="w-full sm:w-auto"
                data-testid="hero-book-button"
              >
                <Calendar className="mr-2 h-4 w-4" />
                Book Service
              </Button>
              {offersConsultations && (
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => navigate(`/provider/${slug}/consultation`)}
                  className="w-full sm:w-auto"
                  data-testid="hero-consultation-button"
                >
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Book Consultation
                </Button>
              )}
            </div>
          </div>
          
          {/* Quick Info */}
          <div className="flex flex-wrap gap-4 mt-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              <span>{provider.city}, {provider.state}</span>
            </div>
            <div className="flex items-center gap-1">
              <Phone className="h-4 w-4" />
              <a href={`tel:${provider.phone}`} className="hover:underline" data-testid="provider-phone">
                {provider.phone}
              </a>
            </div>
            <div className="flex items-center gap-1">
              <MapPin className="h-4 w-4 text-primary" />
              <span>Service Radius: <strong>{provider.serviceRadiusMiles} miles</strong></span>
            </div>
          </div>
        </div>

        {/* About Section */}
        <section className="py-6 border-b">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <User className="h-5 w-5" />
            About
          </h2>
          <p className="text-muted-foreground leading-relaxed" data-testid="provider-bio">
            {provider.bio}
          </p>
        </section>

        {/* Services Section */}
        <section className="py-6 border-b">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Services & Pricing
          </h2>
          <div className="grid gap-3" data-testid="services-list">
            {services.map((service) => {
              const depositAmount = calculateDepositAmount(service);
              return (
                <div 
                  key={service.id} 
                  className="p-4 border rounded-lg hover:border-primary/50 transition-colors"
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <h3 className="font-semibold">{service.name}</h3>
                      {service.description && (
                        <p className="text-sm text-muted-foreground mt-1">{service.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          <span>{service.durationMinutes} min</span>
                        </div>
                        {service.maxGroupSize && (
                          <span className="inline-flex items-center px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-medium rounded" data-testid={`public-group-badge-${service.id}`}>
                            Up to {service.maxGroupSize} riders
                          </span>
                        )}
                        {service.depositEnabled && depositAmount && (
                          <div className="flex items-center gap-1 text-amber-600">
                            <CreditCard className="h-3 w-3" />
                            <span>{formatPrice(depositAmount)} deposit</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-primary">
                        {formatPrice(service.basePriceCents, service.pricingModel)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Cancellation & Deposit Policy Section */}
        {(provider.cancellationWindowHours || provider.cancellationPolicyText || services.some(s => s.depositEnabled)) && (
          <section className="py-6 border-b">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Policies
            </h2>
            <div className="space-y-4">
              {/* Cancellation Policy */}
              {(provider.cancellationWindowHours || provider.cancellationPolicyText) && (
                <div className="p-4 bg-slate-50 border rounded-lg" data-testid="cancellation-policy">
                  <p className="font-medium text-sm mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    Cancellation Policy
                  </p>
                  {provider.cancellationWindowHours && (
                    <p className="text-sm text-muted-foreground">
                      Appointments canceled within {provider.cancellationWindowHours} hours may result in loss of deposit.
                    </p>
                  )}
                  {provider.cancellationPolicyText && (
                    <p className="text-sm text-muted-foreground mt-1">{provider.cancellationPolicyText}</p>
                  )}
                </div>
              )}
              
              {/* Deposit Policy */}
              {services.some(s => s.depositEnabled) && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="deposit-policy">
                  <p className="font-medium text-sm mb-2 flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-blue-600" />
                    Deposit Policy
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Some services require a deposit to confirm your booking. The deposit amount is shown next to each service.
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Photo Gallery */}
        {galleryPhotos.length > 0 && (
          <section className="py-6 border-b" data-testid="provider-gallery">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Photo Gallery
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {galleryPhotos.map((p) => (
                <div key={p.id} className="aspect-square rounded-lg border overflow-hidden bg-muted" data-testid={`public-gallery-photo-${p.id}`}>
                  <img
                    src={`${API_URL}/api/providers/${provider.id}/gallery/${p.id}/image`}
                    alt={p.caption || `${provider.businessName} photo`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Shared Documents (RW-BUILD-014) */}
        {sharedDocs.length > 0 && (
          <section className="py-6 border-b" data-testid="provider-documents">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Documents
            </h2>
            <ul className="space-y-2">
              {sharedDocs.map((d) => (
                <li key={d.id} data-testid={`public-document-${d.id}`}>
                  <a
                    href={`${API_URL}/api/providers/${provider.id}/documents/${d.id}/file`}
                    className="flex items-center gap-2 rounded-md border p-3 text-sm hover:bg-muted/50 transition-colors"
                    download
                  >
                    <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="font-medium flex-1 truncate">{d.title || d.filename}</span>
                    <Download className="h-4 w-4 text-muted-foreground" />
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Reviews Section */}
        <section className="py-6 border-b">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Star className="h-5 w-5" />
            Reviews ({reviews.length})
          </h2>
          
          {reviews.length === 0 ? (
            <p className="text-muted-foreground text-center py-6">
              No reviews yet. Be the first to leave a review after your service!
            </p>
          ) : (
            <div className="space-y-4" data-testid="reviews-list">
              {displayedReviews.map((review) => (
                <div key={review.id} className="p-4 border rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold">{review.clientName}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <StarRating rating={review.rating} size="sm" />
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(review.createdAt), 'MMM d, yyyy')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{review.comment}</p>
                  
                  {review.providerReply && (
                    <div className="mt-3 p-3 bg-muted rounded-lg">
                      <p className="text-xs font-medium text-primary">Response from {provider.businessName}:</p>
                      <p className="text-sm text-muted-foreground mt-1">{review.providerReply}</p>
                    </div>
                  )}
                </div>
              ))}
              
              {reviews.length > 3 && (
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => setShowAllReviews(!showAllReviews)}
                >
                  {showAllReviews ? (
                    <>
                      <ChevronUp className="mr-2 h-4 w-4" />
                      Show Less
                    </>
                  ) : (
                    <>
                      <ChevronDown className="mr-2 h-4 w-4" />
                      Show All {reviews.length} Reviews
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </section>

        {/* Booking Form Section */}
        <section className="py-6" ref={bookingFormRef}>
          <Card className="border-2 border-primary/20">
            <CardHeader className="bg-primary/5">
              <CardTitle className="text-xl flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Book Appointment
              </CardTitle>
              <CardDescription>
                Fill out the form below to request a booking
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Service Selection */}
                <div className="space-y-2">
                  <Label htmlFor="service">Select Service *</Label>
                  <Select value={selectedService} onValueChange={(value) => {
                    setSelectedService(value);
                    setSelectedTime('');
                  }}>
                    <SelectTrigger id="service" data-testid="service-select">
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name} - {formatPrice(service.basePriceCents, service.pricingModel)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Rider count — group services (trail rides, group lessons) */}
                {getSelectedServiceData()?.maxGroupSize && (
                  <div className="space-y-2">
                    <Label htmlFor="riderCount">Number of Riders * <span className="text-xs text-muted-foreground">(up to {getSelectedServiceData().maxGroupSize})</span></Label>
                    <Input
                      id="riderCount"
                      data-testid="rider-count-input"
                      type="number"
                      min="1"
                      max={getSelectedServiceData().maxGroupSize}
                      value={riderCount}
                      onChange={(e) => setRiderCount(e.target.value)}
                      placeholder="How many riders?"
                      required
                    />
                  </div>
                )}

                {/* Zone Selection */}
                {hasRouteZones && (
                  <div className="space-y-2">
                    <Label htmlFor="zone">Your Location/Town *</Label>
                    <Select value={selectedZone} onValueChange={(value) => {
                      setSelectedZone(value);
                      setSelectedDate('');
                      setSelectedTime('');
                      setAvailableSlots([]);
                      setRouteZoneMessage('');
                    }}>
                      <SelectTrigger id="zone" data-testid="zone-select">
                        <SelectValue placeholder="Select your town or area" />
                      </SelectTrigger>
                      <SelectContent>
                        {routeZones.map((zone) => (
                          <SelectItem key={zone.id} value={zone.zoneName}>
                            {zone.zoneName}
                          </SelectItem>
                        ))}
                        <SelectItem value="other">Other / Not Listed</SelectItem>
                      </SelectContent>
                    </Select>
                    {selectedZone && selectedZone !== 'other' && (
                      <p className="text-xs text-muted-foreground">
                        {(() => {
                          const zone = routeZones.find(z => z.zoneName === selectedZone);
                          if (zone) {
                            const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                            return `Service day: ${days[zone.dayOfWeek]}`;
                          }
                          return '';
                        })()}
                      </p>
                    )}
                  </div>
                )}

                {/* Date Selection — Visual booking calendar (Session 27) */}
                <div className="space-y-2">
                  <Label>Select Date *</Label>
                  <BookingCalendar
                    providerId={provider?.id}
                    value={selectedDate}
                    onSelectDate={(iso) => {
                      setSelectedDate(iso);
                      setSelectedTime('');
                    }}
                  />
                  {hasRouteZones && !selectedZone && (
                    <p className="text-xs text-amber-600">Tip: pick your location above to see your route day highlighted</p>
                  )}
                </div>

                {/* Route Zone Message */}
                {routeZoneMessage && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                    <p className="text-sm text-amber-800">{routeZoneMessage}</p>
                  </div>
                )}

                {/* Time Slots */}
                {selectedDate && !routeZoneMessage && (
                  <div className="space-y-2">
                    <Label>Select Time *</Label>
                    {loadingSlots ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : availableSlots.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-3">
                        No available times for this date. Please try another date.
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {availableSlots.map((slot) => (
                          <Button
                            key={slot}
                            type="button"
                            variant={selectedTime === slot ? "default" : "outline"}
                            size="sm"
                            onClick={() => setSelectedTime(slot)}
                            data-testid={`time-slot-${slot}`}
                          >
                            {slot}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Riding Instructor Fields */}
                {isRidingInstructor && (
                  <div className="space-y-4 p-4 bg-muted rounded-lg">
                    <p className="font-medium text-sm">Lesson Details</p>
                    <div className="space-y-2">
                      <Label htmlFor="studentName">Student Name</Label>
                      <Input
                        id="studentName"
                        value={studentName}
                        onChange={(e) => setStudentName(e.target.value)}
                        placeholder="Name of the student"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="studentLevel">Experience Level</Label>
                      <Select value={studentExperienceLevel} onValueChange={setStudentExperienceLevel}>
                        <SelectTrigger id="studentLevel">
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BEGINNER">Beginner</SelectItem>
                          <SelectItem value="INTERMEDIATE">Intermediate</SelectItem>
                          <SelectItem value="ADVANCED">Advanced</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lessonNotes">Lesson Notes</Label>
                      <Textarea
                        id="lessonNotes"
                        value={lessonNotes}
                        onChange={(e) => setLessonNotes(e.target.value)}
                        placeholder="Any specific goals or concerns..."
                        rows={2}
                      />
                    </div>
                  </div>
                )}

                {/* Client Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="clientName">Your Name *</Label>
                    <Input
                      id="clientName"
                      data-testid="client-name-input"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="Full name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="clientPhone">Phone *</Label>
                    <Input
                      id="clientPhone"
                      data-testid="client-phone-input"
                      type="tel"
                      value={clientPhone}
                      onChange={(e) => setClientPhone(e.target.value)}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clientEmail">Email (optional)</Label>
                  <Input
                    id="clientEmail"
                    type="email"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    placeholder="email@example.com"
                  />
                </div>

                {/* Location */}
                <div className="space-y-2">
                  <Label htmlFor="locationAddress">Service Location *</Label>
                  <Input
                    id="locationAddress"
                    data-testid="location-input"
                    value={locationAddress}
                    onChange={(e) => setLocationAddress(e.target.value)}
                    placeholder="Full address or location name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="locationNotes">Location Notes (optional)</Label>
                  <Textarea
                    id="locationNotes"
                    value={locationNotes}
                    onChange={(e) => setLocationNotes(e.target.value)}
                    placeholder="Gate codes, directions, etc."
                    rows={2}
                  />
                </div>

                {/* Animal Selection */}
                <div className="space-y-2">
                  <Label>Animals</Label>
                  <AnimalPicker value={animalSelection} onChange={setAnimalSelection} />
                </div>

                {/* Optional: legacy horse details for farriers/trainers/vets */}
                {isFarrierOrTrainer && animalSelection.mode === 'group' && (
                  <div className="space-y-4 p-4 bg-muted rounded-lg">
                    <p className="font-medium text-sm">Animal Details (optional)</p>
                    <div className="space-y-2">
                      <Label htmlFor="horseName">Animal Name(s)</Label>
                      <Input
                        id="horseName"
                        value={horseName}
                        onChange={(e) => setHorseName(e.target.value)}
                        placeholder="e.g., Bella, Dusty, Copper"
                        data-testid="horse-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="horseNotes">Animal Notes</Label>
                      <Textarea
                        id="horseNotes"
                        value={horseNotes}
                        onChange={(e) => setHorseNotes(e.target.value)}
                        placeholder="Any special notes about the animals..."
                        rows={2}
                        data-testid="horse-notes-input"
                      />
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes">Additional Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any special requirements..."
                    rows={3}
                  />
                </div>

                {/* Deposit Notice */}
                {selectedService && (() => {
                  const service = getSelectedServiceData();
                  const depositAmount = calculateDepositAmount(service);
                  if (service?.depositEnabled && depositAmount) {
                    return (
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="font-medium text-sm text-blue-900 mb-1">Deposit Required</p>
                        <p className="text-sm text-blue-800">
                          A deposit of {formatPrice(depositAmount)} is required to confirm this booking.
                          {service.depositType === 'PERCENTAGE' && ` (${service.depositPercentage}% of service price)`}
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Cancellation Policy Notice */}
                {(provider.cancellationWindowHours || provider.cancellationPolicyText) && (
                  <div className="p-4 bg-slate-50 border rounded-lg" data-testid="booking-cancellation-notice">
                    <p className="font-medium text-sm mb-1">Cancellation Policy:</p>
                    {provider.cancellationWindowHours && (
                      <p className="text-sm text-muted-foreground">
                        Appointments canceled within {provider.cancellationWindowHours} hours may result in loss of deposit.
                      </p>
                    )}
                    {provider.cancellationPolicyText && (
                      <p className="text-sm text-muted-foreground mt-1">{provider.cancellationPolicyText}</p>
                    )}
                  </div>
                )}

                {/* Account Inactive Notice */}
                {!isProviderActive && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="provider-inactive-notice">
                    <p className="font-medium text-sm text-amber-900">Provider account not currently active.</p>
                    <p className="text-sm text-amber-800 mt-1">
                      This provider is not currently accepting online bookings. Please contact them directly.
                    </p>
                  </div>
                )}

                {/* CAPTCHA */}
                <TurnstileWidget onToken={handleTurnstileToken} className="flex justify-center" />

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full h-12 text-base"
                  disabled={submitting || !isProviderActive || !selectedService || !selectedDate || !selectedTime || !clientName || !clientPhone || !locationAddress || !turnstileToken}
                  data-testid="submit-booking-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Submitting...
                    </>
                  ) : !isProviderActive ? (
                    <>
                      Booking Unavailable
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-5 w-5" />
                      Send Booking Request
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </section>

        {/* Bottom CTA */}
        <div className="py-6 text-center">
          <p className="text-muted-foreground mb-4">
            Have questions? Call {provider.businessName} directly
          </p>
          <Button variant="outline" size="lg" asChild>
            <a href={`tel:${provider.phone}`}>
              <Phone className="mr-2 h-4 w-4" />
              {provider.phone}
            </a>
          </Button>
        </div>
      </div>

      {/* Fixed Bottom Book Button (Mobile) */}
      <div className="fixed bottom-0 left-0 right-0 p-4 pb-[72px] bg-background border-t sm:hidden z-40">
        <Button 
          className="w-full h-12" 
          onClick={scrollToBooking}
          data-testid="mobile-book-button"
        >
          <Calendar className="mr-2 h-4 w-4" />
          Book Appointment
        </Button>
      </div>
    </div>
  );
}
